import { Box } from 'grommet';
import { useEffect, useState } from 'react';
import { APIProvider } from '@vis.gl/react-google-maps';
import { GoogleMap } from '..';
import { AutocompleteCustom } from './AutocompleteCustom';
import { useProjectStore } from '../../../context';
import { getAddressObject, IAddress, stateAbbreviations } from '../../../utils/decodeGoogle';
import { toast } from 'react-toastify';

interface ILocationProps {
    place?: IAddress
    id: string;
    updatePlace: (place: IAddress | undefined) => void;
    showMap?: boolean;
}

export const Location = (props: ILocationProps) => {
  const { place, id, updatePlace, showMap = true } = props;
  const { street_address, city, state, zip, lat, lng } = place ?? {};
  const projectStore = useProjectStore();
  const [apiKey, setApiKey] = useState<string | undefined>(undefined);

  useEffect(() => {
    const getApiKey = async () => {
      const apiKey = await projectStore.getMapApiKey();
      setApiKey(apiKey);
    };
    getApiKey();
  }, []);

  const extractAddress = async (place: google.maps.places.Place) => {
    if (!place.addressComponents) {
      toast.error('No address components found in the selected place.');
      return;
    }
    const address = getAddressObject(place.addressComponents);
    address.lat = place.location?.lat();
    address.lng = place.location?.lng();
    if (!address.state) {
      toast.error('State is required to update the location.');
      return;
    }
    address.state = stateAbbreviations[address.state.toLowerCase()] || address.state;
    updatePlace(address);
  };

  return (
    <Box direction="column" width="100%">
      {apiKey && (
        <APIProvider apiKey={apiKey} libraries={['places', 'marker']}>
          <Box gap="small" width="100%">
            <Box width="100%">
              <AutocompleteCustom
                onPlaceSelect={(place) => {
                  if (place) {
                    extractAddress(place);
                  } else {
                    updatePlace(undefined);
                  }
                }}
                defaultValue={
                  [street_address?.trim(), city, state, zip].filter(Boolean).join(', ')
                }
              />
            </Box>
            {showMap && lat !== undefined && lng !== undefined && (
              <GoogleMap
                apiKey={apiKey}
                position={{ lat: lat, lng: lng }}
                zoom={10}
                id={id || 'new'}
              />
            )}
          </Box>
        </APIProvider>
      )}
    </Box>
  );};
